import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  ExperimentOutlined,
  RightOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  message, Modal,
  Row,
  Space,
  Spin,
  Tag,
  Timeline,
} from 'antd';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { inhouseSyncMonitorService } from '../service/InhouseSyncMonitorService';
import {
  IngestSourceDisplay,
  MetadataMismatch,
  ServiceName,
  ServiceNameDisplay,
  TestSyncResponse,
  VideoSyncStatusColor,
  VideoSyncStatusDisplay,
  VideoSyncStatusIcon,
  VideoSyncTracker,
} from '../types/videoSyncMonitorType';
import './InhouseSyncMonitorDetail.css';

// UTC를 PST(UTC-8)로 변환하는 함수
const toPST = (utcTime: string | null, format: string = 'YYYY-MM-DD HH:mm:ss') => {
  if (!utcTime) return '-';
  return moment.utc(utcTime).utcOffset(-8).format(format) + ' PST';
};

const getServiceColor = (service: ServiceName) => {
  const colors: Record<ServiceName, string> = {
    odk: 'cyan',
    odc: 'geekblue',
    amasian: 'purple',
  };
  return colors[service] || 'default';
};

const InhouseSyncMonitorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [tracker, setTracker] = useState<VideoSyncTracker | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestSyncResponse | null>(null);
  const [showAllUrls, setShowAllUrls] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await inhouseSyncMonitorService.fetchById(Number(id));
      setTracker(result);
    } catch (error) {
      console.error('Failed to fetch tracker:', error);
      Modal.error({
        title: 'Error',
        content: 'Failed to load data.',
        okText: 'Confirm',
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTest = async () => {
    if (!tracker) return;
    setTesting(true);
    try {
      const result = await inhouseSyncMonitorService.testSyncByTracker({
        tracker_id: tracker.id,
      });
      setTestResult(result);
      message.success('Test completed');
    } catch (error) {
      console.error('Failed to test:', error);
      Modal.error({
        title: 'Error',
        content: 'Test failed.',
        okText: 'Confirm',
      });
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, React.ReactNode> = {
      pending: <ClockCircleOutlined />,
      synced: <CheckCircleOutlined />,
      failed: <CloseCircleOutlined />,
      alert_sent: <ExclamationCircleOutlined />,
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  if (loading) {
    return (
      <Component.BasePageComponent>
        <div style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Component.BasePageComponent>
    );
  }

  if (!tracker) {
    return (
      <Component.BasePageComponent>
        <div style={{ textAlign: 'center', padding: 100 }}>데이터를 찾을 수 없습니다.</div>
      </Component.BasePageComponent>
    );
  }

  return (
    <Component.BasePageComponent>
      <div className="video-sync-detail-container">
        {/* Header */}
        <div className="detail-header">
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/inhouse-sync-monitor')}>
              Back
            </Button>
          </Space>
          <Space>
            <Button icon={<ExperimentOutlined />} onClick={handleTest} loading={testing}>
              Test Now
            </Button>
          </Space>
        </div>

        {/* Status Banner */}
        <Card className={`status-banner status-${tracker.status}`}>
          <Space size="large">
            <span className="status-icon">{getStatusIcon(tracker.status)}</span>
            <div>
              <div className="status-label">Status</div>
              <Tag
                color={VideoSyncStatusColor[tracker.status]}
                style={{ fontSize: 16, padding: '4px 12px' }}
              >
                {VideoSyncStatusIcon[tracker.status]} {VideoSyncStatusDisplay[tracker.status]}
              </Tag>
            </div>
            {tracker.error_message && (
              <div className="error-message">
                <div className="error-label">Error</div>
                <div>{tracker.error_message}</div>
              </div>
            )}
          </Space>
        </Card>

        <Row gutter={[16, 16]}>
          {/* Content Information */}
          <Col xs={24} lg={12}>
            <Card title="Content Information" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Content ID">
                  <a href={`/episode/edit/${tracker.content_id}`} target="_blank" rel="noreferrer">
                    {tracker.content_id}
                  </a>
                </Descriptions.Item>
                <Descriptions.Item label="Title">{tracker.content_title || '-'}</Descriptions.Item>
                <Descriptions.Item label="Slug">{tracker.content_slug || '-'}</Descriptions.Item>
                <Descriptions.Item label="Content Status">
                  {tracker.content_status || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Season">
                  {tracker.season_title
                    ? `${tracker.season_title} (ID: ${tracker.season_id})`
                    : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* Video Information */}
          <Col xs={24} lg={12}>
            <Card title="Video Information" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Video ID">{tracker.video_id}</Descriptions.Item>
                <Descriptions.Item label="Filename">
                  {tracker.video_filename || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Video Status">
                  {tracker.video_status || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="ODK Episode ID">
                  {tracker.odk_episode_id || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="ODK Video Status">
                  <Tag color={tracker.odk_video_status === 'completed' ? 'green' : 'orange'}>
                    {tracker.odk_video_status || '-'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* Preset Sync Status */}
          <Col xs={24}>
            <Card title="Preset Sync Status" size="small">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <div className="preset-section">
                    <div className="preset-label">Expected Presets</div>
                    <div className="preset-tags">
                      {tracker.expected_presets?.length > 0 ? (
                        tracker.expected_presets.map(preset => (
                          <Tag key={preset} color="blue">
                            {preset}
                          </Tag>
                        ))
                      ) : (
                        <span className="no-data">-</span>
                      )}
                    </div>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="preset-section">
                    <div className="preset-label">Found Presets</div>
                    <div className="preset-tags">
                      {tracker.found_presets?.length > 0 ? (
                        tracker.found_presets.map(preset => (
                          <Tag key={preset} color="green" icon={<CheckCircleOutlined />}>
                            {preset}
                          </Tag>
                        ))
                      ) : (
                        <span className="no-data">-</span>
                      )}
                    </div>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="preset-section">
                    <div className="preset-label">Missing Presets</div>
                    <div className="preset-tags">
                      {tracker.missing_presets?.length > 0 ? (
                        tracker.missing_presets.map(preset => (
                          <Tag key={preset} color="red" icon={<CloseCircleOutlined />}>
                            {preset}
                          </Tag>
                        ))
                      ) : (
                        <span className="no-data">None</span>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* URL Comparison */}
          {(tracker.url_mismatches?.length ||
            tracker.expected_sources ||
            tracker.actual_sources) && (
            <Col xs={24}>
              <Card
                title={
                  <Space>
                    URL Comparison
                    {tracker.url_mismatches && tracker.url_mismatches.length > 0 && (
                      <Tag color="red" icon={<WarningOutlined />}>
                        {tracker.url_mismatches.length} Mismatch
                        {tracker.url_mismatches.length > 1 ? 'es' : ''}
                      </Tag>
                    )}
                  </Space>
                }
                size="small"
                className="url-comparison-card"
              >
                {/* URL Mismatches */}
                {tracker.url_mismatches && tracker.url_mismatches.length > 0 && (
                  <div className="url-mismatches-section">
                    <div className="section-title">
                      <WarningOutlined style={{ color: '#ff4d4f' }} /> URL Mismatches
                    </div>
                    {tracker.url_mismatches.map((mismatch, index) => (
                      <div key={index} className="url-mismatch-item">
                        <div className="mismatch-header">
                          <Tag color="volcano">{mismatch.type}</Tag>
                          <Tag color="blue">{mismatch.preset}</Tag>
                        </div>
                        <div className="url-row">
                          <span className="url-label">Expected:</span>
                          <code className="url-value">{mismatch.expected || '-'}</code>
                        </div>
                        <div className="url-row url-row-actual">
                          <span className="url-label">Actual:</span>
                          <code className="url-value url-mismatch">
                            {mismatch.actual || '(missing)'}
                          </code>
                          <WarningOutlined className="mismatch-icon" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Show All URLs Toggle */}
                <Button
                  type="link"
                  onClick={() => setShowAllUrls(!showAllUrls)}
                  icon={showAllUrls ? <DownOutlined /> : <RightOutlined />}
                  style={{ padding: 0, marginTop: 12 }}
                >
                  {showAllUrls ? 'Hide All URLs' : 'Show All URLs'}
                </Button>

                {/* All URLs Table */}
                {showAllUrls && (
                  <div className="all-urls-section">
                    <Divider style={{ margin: '12px 0' }} />

                    {/* Sources */}
                    {(tracker.expected_sources || tracker.actual_sources) && (
                      <div className="url-table-section">
                        <div className="section-title">Sources</div>
                        <div className="url-table">
                          <div className="url-table-header">
                            <div className="url-table-cell">Preset</div>
                            <div className="url-table-cell">Expected</div>
                            <div className="url-table-cell">Actual</div>
                            <div className="url-table-cell url-table-cell-status">Status</div>
                          </div>
                          {Object.keys({
                            ...tracker.expected_sources,
                            ...tracker.actual_sources,
                          }).map(preset => {
                            const expected = tracker.expected_sources?.[preset];
                            const actual = tracker.actual_sources?.[preset];
                            const isMatch = expected === actual;
                            return (
                              <div key={preset} className="url-table-row">
                                <div className="url-table-cell">
                                  <Tag>{preset}</Tag>
                                </div>
                                <div className="url-table-cell url-cell-value">
                                  <code>{expected || '-'}</code>
                                </div>
                                <div className="url-table-cell url-cell-value">
                                  <code>{actual || '-'}</code>
                                </div>
                                <div className="url-table-cell url-table-cell-status">
                                  {isMatch ? (
                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                  ) : (
                                    <WarningOutlined style={{ color: '#ff4d4f' }} />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Manifests */}
                    {(tracker.expected_manifests || tracker.actual_manifests) && (
                      <div className="url-table-section">
                        <div className="section-title">Manifests</div>
                        <div className="url-table">
                          <div className="url-table-header">
                            <div className="url-table-cell">Preset</div>
                            <div className="url-table-cell">Expected</div>
                            <div className="url-table-cell">Actual</div>
                            <div className="url-table-cell url-table-cell-status">Status</div>
                          </div>
                          {Object.keys({
                            ...tracker.expected_manifests,
                            ...tracker.actual_manifests,
                          }).map(preset => {
                            const expected = tracker.expected_manifests?.[preset];
                            const actual = tracker.actual_manifests?.[preset];
                            const isMatch = expected === actual;
                            return (
                              <div key={preset} className="url-table-row">
                                <div className="url-table-cell">
                                  <Tag>{preset}</Tag>
                                </div>
                                <div className="url-table-cell url-cell-value">
                                  <code>{expected || '-'}</code>
                                </div>
                                <div className="url-table-cell url-cell-value">
                                  <code>{actual || '-'}</code>
                                </div>
                                <div className="url-table-cell url-table-cell-status">
                                  {isMatch ? (
                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                  ) : (
                                    <WarningOutlined style={{ color: '#ff4d4f' }} />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </Col>
          )}

          {/* Metadata Mismatches */}
          {tracker.metadata_mismatches && tracker.metadata_mismatches.length > 0 && (
            <Col xs={24}>
              <Card
                title={
                  <Space>
                    Metadata Comparison
                    <Tag color="red" icon={<WarningOutlined />}>
                      {tracker.metadata_mismatches.length} Mismatch
                      {tracker.metadata_mismatches.length > 1 ? 'es' : ''}
                    </Tag>
                  </Space>
                }
                size="small"
                className="url-comparison-card"
              >
                <div className="url-table">
                  <div className="url-table-header">
                    <div className="url-table-cell">Field</div>
                    <div className="url-table-cell">UCMS Value</div>
                    <div className="url-table-cell">ODK Value</div>
                    <div className="url-table-cell url-table-cell-status">Status</div>
                  </div>
                  {tracker.metadata_mismatches.map((mismatch: MetadataMismatch, index: number) => (
                    <div key={index} className="url-table-row">
                      <div className="url-table-cell">
                        <Tag color="volcano">{mismatch.field}</Tag>
                      </div>
                      <div className="url-table-cell url-cell-value">
                        <code>{mismatch.ucms_value || '-'}</code>
                      </div>
                      <div className="url-table-cell url-cell-value">
                        <code>{mismatch.odk_value || '-'}</code>
                      </div>
                      <div className="url-table-cell url-table-cell-status">
                        <WarningOutlined style={{ color: '#ff4d4f' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          )}

          {/* Sync Information */}
          <Col xs={24} lg={12}>
            <Card title="Sync Information" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Ingest Source">
                  <Tag color="green">{IngestSourceDisplay[tracker.ingest_source]}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Service">
                  <Tag color={getServiceColor(tracker.service_name)}>
                    {ServiceNameDisplay[tracker.service_name]}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Check Count">
                  <Tag>{tracker.check_count}</Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* Timeline */}
          <Col xs={24} lg={12}>
            <Card title="Timeline" size="small">
              <Timeline
                items={[
                  {
                    color: 'blue',
                    children: (
                      <div>
                        <strong>Created</strong>
                        <div>{toPST(tracker.created)}</div>
                      </div>
                    ),
                  },
                  {
                    color: 'orange',
                    children: (
                      <div>
                        <strong>Expected Check Time</strong>
                        <div>{toPST(tracker.expected_check_time)}</div>
                      </div>
                    ),
                  },
                  ...(tracker.last_checked_at
                    ? [
                        {
                          color: tracker.status === 'synced' ? 'green' : 'red',
                          children: (
                            <div>
                              <strong>Last Checked</strong>
                              <div>{toPST(tracker.last_checked_at)}</div>
                            </div>
                          ),
                        },
                      ]
                    : []),
                  {
                    color: 'gray',
                    children: (
                      <div>
                        <strong>Last Modified</strong>
                        <div>{toPST(tracker.modified)}</div>
                      </div>
                    ),
                  },
                ]}
              />
            </Card>
          </Col>

          {/* Test Result */}
          {testResult && (
            <Col xs={24}>
              <Card
                title={
                  <Space>
                    <ExperimentOutlined />
                    Test Result
                    {testResult.synced ? (
                      <Tag color="green">Synced</Tag>
                    ) : (
                      <Tag color="red">Not Synced</Tag>
                    )}
                  </Space>
                }
                size="small"
                className="test-result-card"
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Episode ID">
                        {testResult.episode_id || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Episode Title">
                        {testResult.episode_title || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Video Status">
                        <Tag color={testResult.video_status === 'completed' ? 'green' : 'orange'}>
                          {testResult.video_status || '-'}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="preset-section">
                      <div className="preset-label">Found Presets</div>
                      <div className="preset-tags">
                        {testResult.found_presets?.map(preset => (
                          <Tag key={preset} color="green" icon={<CheckCircleOutlined />}>
                            {preset}
                          </Tag>
                        ))}
                      </div>
                    </div>
                    <Divider style={{ margin: '12px 0' }} />
                    <div className="preset-section">
                      <div className="preset-label">Missing Presets</div>
                      <div className="preset-tags">
                        {testResult.missing_presets?.length > 0 ? (
                          testResult.missing_presets.map(preset => (
                            <Tag key={preset} color="red" icon={<CloseCircleOutlined />}>
                              {preset}
                            </Tag>
                          ))
                        ) : (
                          <Tag color="green">None</Tag>
                        )}
                      </div>
                    </div>
                  </Col>
                  {testResult.reason && (
                    <Col xs={24}>
                      <div className="reason-box">
                        <strong>Reason:</strong> {testResult.reason}
                      </div>
                    </Col>
                  )}
                </Row>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(InhouseSyncMonitorDetail);
